import Table from '@storeblocks/table';
import { type FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import { OrganizationUser } from '@/api/organizationUsers/models/OrganizationUser';
import { Permission } from '@/api/permissions/models/Permission';
import { EditAndDeleteButtons } from '@/components/EditAndDeleteButtons';
import { useHasPermission } from '@/components/Permissions/useHasPermission';
import { useTexts } from '@/hooks/useTexts';

import { EditOrganizationUser } from './AddAndEditUserModal/EditOrganizationUsers';
import { DeleteOrganizationUserModal } from './DeleteUserModal';
import {
  OrganizationActiveUserColumn,
  OrganizationActiveUserRow,
} from './rowAndColumnTypes';

interface Props {
  organizationUsers: OrganizationUser[];
}

export const OrganizationActiveUsersTable: FC<Props> = ({
  organizationUsers,
}) => {
  const texts = useTexts();

  const hasPermission = useHasPermission();
  const hasAdminWriteRole = hasPermission(Permission.UserAdminWrite);

  const [tableRows, setTableRows] = useState<OrganizationActiveUserRow[]>([]);
  const [userToEdit, setUserToEdit] = useState<OrganizationUser | undefined>();
  const [userToDelete, setUserToDelete] = useState<
    OrganizationUser | undefined
  >();

  useEffect(() => {
    const rows = createRows(organizationUsers);
    setTableRows(rows);
  }, [organizationUsers, hasAdminWriteRole]);

  const createRows = (users: OrganizationUser[]): OrganizationActiveUserRow[] =>
    users.map((user) => ({
      name: `${user.givenName} ${user.familyName}`,
      email: user.email || '-',
      // TODO : add this from backend
      roles: '-',
      actions: {
        displayContent: (
          <EditAndDeleteButtons
            disabled={!hasAdminWriteRole || !user.userId}
            onEdit={() => setUserToEdit(user)}
            onDelete={() => setUserToDelete(user)}
            alignRight
            actionContext="organization-user"
          />
        ),
        sortContent: '',
      },
    }));

  const tableColumns: OrganizationActiveUserColumn[] = [
    {
      key: 'name',
      name: texts.contact.name,
      dataType: 'text',
    },
    {
      key: 'email',
      name: texts.email,
      dataType: 'text',
    },
    {
      key: 'roles',
      name: texts.roles,
      dataType: 'text',
    },
    {
      key: 'actions',
      name: '',
      dataType: 'other',
      renderType: 'jsx',
      style: { textAlign: 'right' },
    },
  ];

  return (
    <>
      <StyledTable data={tableRows} columns={tableColumns} sortable />

      {userToEdit && (
        <EditOrganizationUser
          show={!!userToEdit}
          onClose={() => setUserToEdit(undefined)}
          userToEdit={userToEdit}
        />
      )}
      {userToDelete && (
        <DeleteOrganizationUserModal
          show={!!userToDelete}
          onClose={() => setUserToDelete(undefined)}
          user={userToDelete}
        />
      )}
    </>
  );
};

const StyledTable = styled(Table)`
  // Name.
  & th:nth-of-type(1) {
    width: 33%;
    max-width: 33%;
  }

  // Email.
  & th:nth-of-type(2) {
    width: 20%;
    max-width: 20%;
  }

  // Roles.
  & th:nth-of-type(3) {
    width: 30%;
    max-width: 30%;
  }

  // Action buttons.
  & th:nth-of-type(4) {
    width: 17%;
    max-width: 17%;
  }
`;
